import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Upload, Download, Plus, Edit, Trash2, Eye, Search,
    CheckCircle, BookOpen, Users, FileSpreadsheet, X, Check
} from 'lucide-react'
import { toast } from 'sonner'
import { mockQuestionBanks, QuestionBank, chaptersBySubject, currentMentor, Subject } from '@/data/mentor-mock-data'

// Mock data imported from mentor-mock-data.ts

// Get subjects based on mentor assignment
const getAvailableSubjects = (): Subject[] => {
    return currentMentor.assignedSubjects
}

const courseOptions = ['Crash Course', 'Long Term', 'Short Term', '2-Year Integrated', 'Dropper']

export function QuestionBankManagementView() {
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedSubject, setSelectedSubject] = useState<Subject | 'All'>('All')
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [showPublishModal, setShowPublishModal] = useState(false)
    const [selectedBank, setSelectedBank] = useState<QuestionBank | null>(null)
    const [publishTargets, setPublishTargets] = useState<string[]>([])

    // New bank form state
    const [newBankTitle, setNewBankTitle] = useState('')
    const [newBankSubject, setNewBankSubject] = useState<Subject>(getAvailableSubjects()[0])
    const [newBankChapter, setNewBankChapter] = useState('')
    const [newBankCourse, setNewBankCourse] = useState('')
    const [customChapters, setCustomChapters] = useState<string[]>([])
    const [addingChapter, setAddingChapter] = useState(false)
    const [newChapterInput, setNewChapterInput] = useState('')

    // Get available subjects for this mentor
    const availableSubjects = getAvailableSubjects()
    const subjectsFilter = ['All', ...availableSubjects]
    const uniqueCourses = ['Complete NEET 2025 Preparation', 'Physics Masterclass'] // In real app, derived from mentor's assigned courses

    // Filter banks
    const filteredBanks = mockQuestionBanks.filter(bank => {
        const matchesSearch = bank.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bank.chapter.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesSubject = selectedSubject === 'All' || bank.subject === selectedSubject
        return matchesSearch && matchesSubject
    })

    // Stats
    const stats = {
        total: mockQuestionBanks.length,
        published: mockQuestionBanks.filter(b => b.status === 'published').length,
        totalQuestions: mockQuestionBanks.reduce((sum, b) => sum + b.questionCount, 0),
        draft: mockQuestionBanks.filter(b => b.status === 'draft').length
    }

    const handleDownloadTemplate = () => {
        toast.success('Downloading Excel template...')
        // In production, this would download an actual Excel file
    }

    const handleBulkUpload = () => {
        toast.success('Questions uploaded successfully!')
        setShowUploadModal(false)
        setNewBankTitle('')
        setNewBankChapter('')
        setNewBankCourse('')
    }

    const handlePublish = () => {
        if (publishTargets.length === 0) {
            toast.error('Please select at least one course')
            return
        }
        toast.success(`Question bank published to ${publishTargets.join(', ')}`)
        setShowPublishModal(false)
        setSelectedBank(null)
        setPublishTargets([])
    }

    const handleAddChapter = () => {
        if (!newChapterInput.trim()) return
        setCustomChapters(prev => [...prev, newChapterInput.trim()])
        setNewChapterInput('')
        setAddingChapter(false)
    }

    const getAllChapters = () => {
        return [...(chaptersBySubject[newBankSubject] || []), ...customChapters]
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Question Bank</h2>
                    <p className="text-sm text-theme-secondary">Manage and publish question banks for students</p>
                </div>
                <button
                    onClick={() => setShowUploadModal(true)}
                    className="w-full sm:w-auto px-4 py-2.5 bg-primary-blue text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25"
                >
                    <Upload className="h-4 w-4" />
                    Upload Question Bank
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-theme-primary">{stats.total}</p>
                    <p className="text-xs text-theme-secondary">Total Banks</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{stats.published}</p>
                    <p className="text-xs text-theme-secondary">Published</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{stats.totalQuestions}</p>
                    <p className="text-xs text-theme-secondary">Total Questions</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{stats.draft}</p>
                    <p className="text-xs text-theme-secondary">Drafts</p>
                </GlassmorphicCard>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-secondary" />
                    <input
                        type="text"
                        placeholder="Search question banks..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm focus:border-primary-blue outline-none"
                    />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                    {subjectsFilter.map((sub) => (
                        <button
                            key={sub}
                            onClick={() => setSelectedSubject(sub as Subject | 'All')}
                            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${selectedSubject === sub
                                ? 'bg-primary-blue text-white'
                                : 'bg-white dark:bg-slate-800 text-theme-secondary border border-slate-200 dark:border-slate-700'
                                }`}
                        >
                            {sub}
                        </button>
                    ))}
                </div>
            </div>

            {/* Question Banks List */}
            <div className="space-y-3">
                {filteredBanks.length === 0 ? (
                    <GlassmorphicCard className="p-12 text-center">
                        <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-theme-secondary">No question banks found</p>
                    </GlassmorphicCard>
                ) : (
                    filteredBanks.map((bank, idx) => (
                        <motion.div
                            key={bank.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                        >
                            <GlassmorphicCard className="p-4 hover:shadow-lg transition-all">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    {/* Left: Info */}
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${bank.subject === 'Physics' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-600' :
                                            bank.subject === 'Chemistry' ? 'bg-green-100 dark:bg-green-900/20 text-green-600' :
                                                'bg-purple-100 dark:bg-purple-900/20 text-purple-600'
                                            }`}>
                                            <BookOpen className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-theme-primary truncate">{bank.title}</h3>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${bank.status === 'published'
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                    : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                                    }`}>
                                                    {bank.status}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 mt-1 text-xs text-theme-secondary flex-wrap">
                                                <span>{bank.subject}</span>
                                                <span>•</span>
                                                <span>{bank.chapter}</span>
                                                <span>•</span>
                                                <span>{bank.questionCount} questions</span>
                                                <span className={`px-1.5 py-0.5 rounded ${bank.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                                    bank.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                                        'bg-amber-100 text-amber-700'
                                                    }`}>
                                                    {bank.difficulty}
                                                </span>
                                            </div>
                                            {bank.publishedTo.length > 0 && (
                                                <div className="flex items-center gap-1 mt-1.5 text-xs text-theme-secondary">
                                                    <Users className="h-3 w-3" />
                                                    <span>Published to: {bank.publishedTo.join(', ')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right: Actions */}
                                    <div className="flex items-center gap-2">
                                        <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-secondary">
                                            <Eye className="h-4 w-4" />
                                        </button>
                                        <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-secondary">
                                            <Edit className="h-4 w-4" />
                                        </button>
                                        {bank.status === 'draft' && (
                                            <button
                                                onClick={() => {
                                                    setSelectedBank(bank)
                                                    setShowPublishModal(true)
                                                }}
                                                className="px-3 py-1.5 bg-primary-blue text-white text-xs font-medium rounded-lg hover:bg-blue-600"
                                            >
                                                Publish
                                            </button>
                                        )}
                                        <button className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    ))
                )}
            </div>

            {/* Upload Modal */}
            <AnimatePresence>
                {showUploadModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowUploadModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg p-6"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-theme-primary">Upload Question Bank</h3>
                                <button onClick={() => setShowUploadModal(false)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
                                    <X className="h-5 w-5 text-theme-secondary" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Template Download */}
                                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <FileSpreadsheet className="h-8 w-8 text-green-600" />
                                            <div>
                                                <p className="font-medium text-theme-primary text-sm">Excel Template</p>
                                                <p className="text-xs text-theme-secondary">Download and fill the template</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleDownloadTemplate}
                                            className="px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded-lg hover:bg-green-200 flex items-center gap-1"
                                        >
                                            <Download className="h-3 w-3" />
                                            Download
                                        </button>
                                    </div>
                                </div>

                                {/* Bank Details */}
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Bank Title</label>
                                    <input
                                        type="text"
                                        value={newBankTitle}
                                        onChange={e => setNewBankTitle(e.target.value)}
                                        placeholder="e.g. Mechanics Practice Set 1"
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm outline-none focus:border-primary-blue"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Course</label>
                                    <select
                                        value={newBankCourse}
                                        onChange={e => setNewBankCourse(e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm outline-none"
                                    >
                                        <option value="">Select Course</option>
                                        {uniqueCourses.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Subject</label>
                                        <select
                                            value={newBankSubject}
                                            onChange={e => {
                                                setNewBankSubject(e.target.value as Subject)
                                                setNewBankChapter('')
                                            }}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm outline-none"
                                        >
                                            {availableSubjects.map(subject => (
                                                <option key={subject} value={subject}>{subject}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Chapter</label>
                                        <select
                                            value={newBankChapter}
                                            onChange={e => setNewBankChapter(e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm outline-none"
                                        >
                                            <option value="">Select Chapter</option>
                                            {getAllChapters().map(ch => (
                                                <option key={ch} value={ch}>{ch}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Add Custom Chapter */}
                                <div>
                                    {addingChapter ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={newChapterInput}
                                                onChange={e => setNewChapterInput(e.target.value)}
                                                placeholder="New chapter name"
                                                className="flex-1 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-primary-blue text-sm"
                                                autoFocus
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') handleAddChapter()
                                                    if (e.key === 'Escape') setAddingChapter(false)
                                                }}
                                            />
                                            <button onClick={handleAddChapter} className="p-2 bg-primary-blue text-white rounded-lg">
                                                <Check className="h-4 w-4" />
                                            </button>
                                            <button onClick={() => setAddingChapter(false)} className="p-2 bg-slate-200 dark:bg-slate-700 rounded-lg">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setAddingChapter(true)}
                                            className="text-sm text-primary-blue hover:underline flex items-center gap-1"
                                        >
                                            <Plus className="h-3 w-3" />
                                            Add new chapter
                                        </button>
                                    )}
                                </div>

                                {/* File Upload */}
                                <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-6 text-center">
                                    <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                                    <p className="text-sm text-theme-secondary">Drag and drop your Excel file here, or</p>
                                    <button className="text-sm text-primary-blue hover:underline mt-1">Browse files</button>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setShowUploadModal(false)}
                                        className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-theme-secondary font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleBulkUpload}
                                        className="flex-1 py-2.5 bg-primary-blue text-white rounded-xl font-medium hover:bg-blue-600"
                                    >
                                        Upload & Save
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Publish Modal */}
            <AnimatePresence>
                {showPublishModal && selectedBank && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowPublishModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6"
                        >
                            <h3 className="text-lg font-bold text-theme-primary mb-2">Publish Question Bank</h3>
                            <p className="text-sm text-theme-secondary mb-4">
                                Select which courses should have access to "{selectedBank.title}"
                            </p>

                            <div className="space-y-2 mb-6">
                                {courseOptions.map(course => (
                                    <button
                                        key={course}
                                        onClick={() => {
                                            setPublishTargets(prev =>
                                                prev.includes(course)
                                                    ? prev.filter(c => c !== course)
                                                    : [...prev, course]
                                            )
                                        }}
                                        className={`w-full p-3 rounded-xl text-left text-sm font-medium flex items-center justify-between transition-all ${publishTargets.includes(course)
                                            ? 'bg-primary-blue/10 border-2 border-primary-blue text-primary-blue'
                                            : 'bg-slate-50 dark:bg-slate-800 border-2 border-transparent text-theme-primary hover:border-primary-blue/30'
                                            }`}
                                    >
                                        {course}
                                        {publishTargets.includes(course) && <CheckCircle className="h-5 w-5" />}
                                    </button>
                                ))}
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowPublishModal(false)
                                        setPublishTargets([])
                                    }}
                                    className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-theme-secondary font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePublish}
                                    className="flex-1 py-2.5 bg-primary-blue text-white rounded-xl font-medium hover:bg-blue-600"
                                >
                                    Publish
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
